// client/src/components/history/PublicEventArticle.jsx
//
// Read-only public "article" for one event: name as the header, an optional
// intro paragraph, then the event's public media in chronological order of
// being made public — each with its caption and (if the tenant allows)
// likes / comments. Rendered in whichever of the 6 article templates the
// tenant picked (see constants/articleTemplates.js + styles/articleTemplates.css).
// Media taps open the full-screen viewer.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MediaViewer from '../media/MediaViewer';
import { formatSpelledDate } from '../../utils/eventMedia';
import { DEFAULT_ARTICLE_TEMPLATE } from '../../constants/articleTemplates';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';
const getMemberToken = () => localStorage.getItem(MEMBER_TOKEN_KEY);
const clearMemberSession = () => { localStorage.removeItem(MEMBER_TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); };
const memberHeaders = () => {
  const t = getMemberToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const isVideoMime = (m) => /^video\//i.test(m || '');

export default function PublicEventArticle() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const tenantNum = Number(tenant) || null;

  const [gate, setGate] = useState('loading'); // loading | ok
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [eventName, setEventName] = useState('');
  const [dateLine, setDateLine] = useState('');
  const [intro, setIntro] = useState(null);
  const [template, setTemplate] = useState(DEFAULT_ARTICLE_TEMPLATE);
  const [items, setItems] = useState([]);
  const [allowLikes, setAllowLikes] = useState(true);
  const [allowComments, setAllowComments] = useState(true);

  const [mediaViewer, setMediaViewer] = useState(null);

  // ── Gate: reuse the tenant's public-history config ─────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.success) {
          if (res.data.requireLogin && !getMemberToken()) {
            navigate(`/${tenant}/memberlogin`, { replace: true });
            return;
          }
          setGate('ok');
        } else {
          setErr(res.data?.message || 'Event history is not available.');
          setGate('ok');
        }
      } catch (e) {
        setErr(e.response?.status === 403
          ? 'This event history is not publicly available.'
          : 'Failed to load event history.');
        setGate('ok');
      }
    })();
  }, [tenantNum]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchArticle = useCallback(async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(
        `${baseUrl}/api/events/${tenantNum}/publicHistory/events/${eventId}`,
        { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setEventName(res.data.eventName || '');
        setDateLine(formatSpelledDate(res.data.startdate, res.data.enddate));
        setIntro(res.data.intro || null);
        setTemplate(res.data.template || DEFAULT_ARTICLE_TEMPLATE);
        setItems(res.data.items || []);
        if (typeof res.data.allowLikes === 'boolean') setAllowLikes(res.data.allowLikes);
        if (typeof res.data.allowComments === 'boolean') setAllowComments(res.data.allowComments);
      } else {
        setErr(res.data?.message || 'Event history is not available.');
      }
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 401 && data?.requireLogin) {
        clearMemberSession();
        navigate(`/${tenant}/memberlogin`, { replace: true });
      } else if (status === 402 && data?.requirePayment) {
        navigate(`/${tenant}/publicEventHistory`, { replace: true });
      } else if (status === 404) {
        setErr('This event is not available.');
      } else {
        setErr('Failed to load event history.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantNum, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (gate === 'ok') fetchArticle(); }, [gate, fetchArticle]);

  const handleToggleLike = async (fileId) => {
    if (!getMemberToken()) { navigate(`/${tenant}/memberlogin`); return; }
    setItems((prev) => prev.map((it) => {
      if (it.fileId !== fileId) return it;
      const was = !!it.viewerLiked;
      return { ...it, viewerLiked: !was, likeCount: Math.max(0, (Number(it.likeCount) || 0) + (was ? -1 : 1)) };
    }));
    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${fileId}/like`,
        {}, { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setItems((prev) => prev.map((it) => it.fileId === fileId
          ? { ...it, viewerLiked: !!res.data.liked, likeCount: Number(res.data.likeCount) || 0 }
          : it));
      }
    } catch (e) {
      if (e.response?.status === 401) { clearMemberSession(); navigate(`/${tenant}/memberlogin`); }
    }
  };

  if (gate === 'loading') {
    return <div className="public-article-page"><p style={{ textAlign: 'center', color: '#888' }}>Loading...</p></div>;
  }

  return (
    <div className="public-article-page">
      <article className={`public-article public-article--${template}`}>
        <button type="button" className="public-article__back" onClick={() => navigate(`/${tenant}/publicEventHistory`)}>
          ← All events
        </button>

        {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}

        {err && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>{err}</div>
        )}

        {!loading && !err && (
          <>
            <div className="public-article__header">
              {dateLine && <span className="public-article__kicker">{dateLine}</span>}
              <h1 className="public-article__headline">{eventName}</h1>
              <hr className="public-article__rule" />
            </div>

            {intro && (
              <div className="public-article__intro">
                {String(intro).split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
              </div>
            )}

            {items.length === 0 && (
              <p style={{ textAlign: 'center' }}>Nothing has been published for this event yet.</p>
            )}

            {items.length > 0 && (
              <div className="public-article__media-list">
                {items.map((it) => (
                  <PublicMediaCard
                    key={it.fileId}
                    item={it}
                    tenantNum={tenantNum}
                    tenant={tenant}
                    allowLikes={allowLikes}
                    allowComments={allowComments}
                    canPost={!!getMemberToken()}
                    onExpand={() => setMediaViewer({ url: it.url, isVideo: isVideoMime(it.mimetype) })}
                    onToggleLike={() => handleToggleLike(it.fileId)}
                    onSignInClick={() => navigate(`/${tenant}/memberlogin`)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {mediaViewer && (
          <MediaViewer src={mediaViewer.url} isVideo={mediaViewer.isVideo} onClose={() => setMediaViewer(null)} />
        )}

        <div className="public-article__footer">
          Powered by{' '}
          <a href="https://on-screen.app" target="_blank" rel="noopener noreferrer">On-Screen</a>
        </div>
      </article>
    </div>
  );
}

function PublicMediaCard({
  item, tenantNum, allowLikes, allowComments, canPost,
  onExpand, onToggleLike, onSignInClick,
}) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${item.fileId}/comments`
      );
      if (res.data?.success) setComments(res.data.comments || []);
    } catch { setComments([]); }
  }, [tenantNum, item.fileId]);

  const toggleComments = async () => {
    const willOpen = !showComments;
    setShowComments(willOpen);
    if (willOpen && comments == null) await loadComments();
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    if (!canPost) { onSignInClick(); return; }
    setBusy(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${item.fileId}/comments`,
        { body }, { headers: memberHeaders() }
      );
      if (res.data?.success && res.data.comment) {
        setComments((prev) => [...(prev || []), res.data.comment]);
        setDraft('');
      }
    } catch { /* ignore — resyncs on reload */ } finally { setBusy(false); }
  };

  const isVideo = isVideoMime(item.mimetype);

  return (
    <div className="public-article-card">
      <div
        className="public-article-card__figure"
        role="button"
        tabIndex={0}
        onClick={onExpand}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onExpand()}
      >
        {isVideo ? (
          <video className="public-article-card__media" src={item.url} muted playsInline preload="metadata" />
        ) : (
          <img className="public-article-card__media" src={item.url} alt={item.caption || 'event media'} loading="lazy" />
        )}
      </div>

      {(item.caption || allowLikes || allowComments) && (
        <div className="public-article-card__body">
          {item.caption && <p className="public-article-card__caption">{item.caption}</p>}

          {(allowLikes || allowComments) && (
            <div className="public-article-card__engagement">
              {allowLikes && (
                <button
                  type="button"
                  className="public-article-card__like-btn"
                  onClick={onToggleLike}
                  style={item.viewerLiked ? { color: '#ff4d6d', opacity: 1 } : undefined}
                >
                  <span style={{ fontSize: '1rem' }}>{item.viewerLiked ? '♥' : '♡'}</span>
                  <span>{Number(item.likeCount) || 0}</span>
                </button>
              )}
              {allowComments && (
                <button type="button" className="public-article-card__comment-btn" onClick={toggleComments}>
                  <span style={{ fontSize: '1rem' }}>💬</span>
                  <span>{Number(item.commentCount) || 0}</span>
                </button>
              )}
            </div>
          )}

          {allowComments && showComments && (
            <div className="public-article-card__comments">
              {(comments || []).length === 0 ? (
                <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0 0 0.5rem' }}>No comments yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.5rem' }}>
                  {comments.map((c) => (
                    <div key={c.id} style={{ background: 'rgba(128,128,128,0.12)', padding: '6px 8px', borderRadius: 6, fontSize: '0.85rem' }}>
                      <strong style={{ fontSize: '0.8rem' }}>{c.authorName || 'Member'}</strong>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.body}</div>
                    </div>
                  ))}
                </div>
              )}
              {canPost ? (
                <form onSubmit={submitComment} style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add a comment…"
                    disabled={busy}
                    maxLength={2000}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(128,128,128,0.4)', fontSize: '0.85rem' }}
                  />
                  <button type="submit" disabled={busy || !draft.trim()} style={{ padding: '6px 10px', borderRadius: 4, border: 'none', background: '#ff6800', color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: busy || !draft.trim() ? 0.6 : 1 }}>
                    Post
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={onSignInClick}
                  style={{ background: 'none', border: '1px solid #ff6800', color: '#ff6800', padding: '6px 10px', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', width: '100%' }}
                >
                  Sign in to comment
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
